<template>
  <v-container grid-list-md>
    <v-layout row wrap>
      <v-flex xs12>
        <v-data-table
          :headers="headers"
          :items="pixPaymentList"
          :loading="loadingData"
          :pagination.sync="pagination"
          :total-items="pixPaymentResourcesCount"
          :rows-per-page-items="[10, 25, 50, 100]"
          class="elevation-1"
        >
          <template v-slot:items="props">
            <td class="text-xs-left">{{ formatDate(props.item.created_at) }}</td>
            <td class="text-xs-left">{{ getPixType(props.item.pix_type) }}</td>
            <td class="text-xs-left">{{ props.item.recipient_name }}</td>
            <td class="text-xs-left">{{ formatDocument(props.item.recipient_document) }}</td>
            <td class="text-xs-right">{{ $n(props.item.amount, "currency") }}</td>
            <td class="text-xs-center">
              <v-chip
                :color="getStatusColor(props.item.status)"
                text-color="white"
                small
              >
                {{ getStatusText(props.item.status) }}
              </v-chip>
            </td>
            <td class="text-xs-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" @click="showDetails(props.item)">
                    <v-icon small>visibility</v-icon>
                  </v-btn>
                </template>
                <span>Ver detalhes</span>
              </v-tooltip>
            </td>
          </template>
          <template v-slot:no-data>
            <v-alert :value="true" color="info" icon="info">
              Nenhum pagamento PIX encontrado
            </v-alert>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!-- Modal de Detalhes -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedPayment">
        <v-card-title>
          <span class="headline">Detalhes do Pagamento PIX</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12 sm6>
                <strong>Data:</strong> {{ formatDate(selectedPayment.created_at) }}
              </v-flex>
              <v-flex xs12 sm6>
                <strong>Status:</strong> 
                <v-chip
                  :color="getStatusColor(selectedPayment.status)"
                  text-color="white"
                  small
                >
                  {{ getStatusText(selectedPayment.status) }}
                </v-chip>
              </v-flex>
              <v-flex xs12 sm6>
                <strong>Tipo:</strong> {{ getPixType(selectedPayment.pix_type) }}
              </v-flex>
              <v-flex xs12 sm6>
                <strong>Valor:</strong> {{ $n(selectedPayment.amount, "currency") }}
              </v-flex>
              <v-flex xs12>
                <strong>Destinatário:</strong> {{ selectedPayment.recipient_name }}
              </v-flex>
              <v-flex xs12>
                <strong>CPF/CNPJ:</strong> {{ formatDocument(selectedPayment.recipient_document) }}
              </v-flex>
              <v-flex xs12 v-if="selectedPayment.pix_key">
                <strong>Chave PIX:</strong> {{ selectedPayment.pix_key }}
              </v-flex>
              <v-flex xs12 v-if="selectedPayment.protocol">
                <strong>Protocolo:</strong> {{ selectedPayment.protocol }}
              </v-flex>
              <v-flex xs12 v-if="selectedPayment.description">
                <strong>Descrição:</strong> {{ selectedPayment.description }}
              </v-flex>
              <v-flex xs12 v-if="selectedPayment.error_message">
                <v-alert :value="true" type="error">
                  {{ selectedPayment.error_message }}
                </v-alert>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="detailsDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "PixPaymentTable",
  created() {
    this.loadData();
  },
  data() {
    return {
      headers: [
        { text: "Data", value: "created_at", align: "left" },
        { text: "Tipo", value: "pix_type", align: "left" },
        { text: "Destinatário", value: "recipient_name", align: "left" },
        { text: "CPF/CNPJ", value: "recipient_document", align: "left" },
        { text: "Valor", value: "amount", align: "right" },
        { text: "Status", value: "status", align: "center" },
        { text: "Ações", value: "actions", align: "center", sortable: false }
      ],
      pagination: {
        page: 1,
        rowsPerPage: 25
      },
      detailsDialog: false,
      selectedPayment: null
    };
  },
  computed: {
    ...mapGetters({
      pixPaymentList: "bills/pixPaymentList",
      pixPaymentResourcesCount: "bills/pixPaymentResourcesCount",
      loadingData: "bills/pixPaymentLoadingData"
    })
  },
  watch: {
    pagination: {
      handler() {
        this.loadData();
      },
      deep: true
    }
  },
  methods: {
    loadData() {
      const params = {
        page: this.pagination.page,
        limit: this.pagination.rowsPerPage
      };
      this.$store.dispatch("bills/loadPixPayments", params);
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    formatDocument(doc) {
      if (!doc) return "-";
      if (doc.length === 11) {
        // CPF
        return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      } else if (doc.length === 14) {
        // CNPJ
        return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return doc;
    },
    getPixType(type) {
      const types = {
        "CHAVE_PIX": "Chave PIX",
        "PIX_COPIA_E_COLA": "Copia e Cola",
        "DADOS_BANCARIOS": "Dados Bancários"
      };
      return types[type] || type;
    },
    getStatusColor(status) {
      const colors = {
        "pending": "orange",
        "check_payment": "light-blue",
        "waiting_payment_day": "amber",
        "paid": "green",
        "rejected": "red",
        "unknown": "grey",
        "processing": "blue",
        "confirmed": "green",
        "failed": "red",
        "cancelled": "grey"
      };
      return colors[status] || "grey";
    },
    getStatusText(status) {
      const texts = {
        "pending": "Pendente",
        "check_payment": "Verificando Pagamento",
        "waiting_payment_day": "Aguardando Processamento",
        "paid": "Pago",
        "rejected": "Rejeitado",
        "unknown": "Status Desconhecido",
        "processing": "Processando",
        "confirmed": "Confirmado",
        "failed": "Falhou",
        "cancelled": "Cancelado"
      };
      return texts[status] || status;
    },
    showDetails(payment) {
      this.selectedPayment = payment;
      this.detailsDialog = true;
    }
  }
};
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}
</style>